import { useState } from 'react';
import { CaretDown, CaretRight } from '@phosphor-icons/react';

/**
 * Persisted collapsible section header. Used across Dashboard, Inbox, Facilities
 * to keep busy pages overviewable. Collapse-state is stored in localStorage
 * per `storageKey` so the user's preference is remembered.
 *
 * Usage:
 *   <CollapsibleSection
 *     id="filters"
 *     title="Filters"
 *     icon={Funnel}
 *     defaultCollapsed
 *     storageKey="inbox:collapsed"
 *   >
 *     ...
 *   </CollapsibleSection>
 */
export default function CollapsibleSection({
  id,
  title,
  icon: Icon,
  accent,
  storageKey = 'ui:collapsed',
  defaultCollapsed = true,
  rightAdornment,
  children,
}) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const map = JSON.parse(raw);
        if (typeof map[id] === 'boolean') return map[id];
      }
    } catch { /* ignore */ }
    return defaultCollapsed;
  });

  const toggle = () => {
    setCollapsed(c => {
      const next = !c;
      try {
        const raw = localStorage.getItem(storageKey);
        const map = raw ? JSON.parse(raw) : {};
        map[id] = next;
        localStorage.setItem(storageKey, JSON.stringify(map));
      } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <div className="mb-4" data-testid={`collapsible-${id}`}>
      <div className="flex items-center gap-2 pb-2 border-b border-zinc-200">
        <button
          type="button"
          onClick={toggle}
          data-testid={`collapsible-toggle-${id}`}
          aria-expanded={!collapsed}
          className="flex-1 flex items-center gap-2 text-left text-sm font-heading font-semibold text-zinc-700 hover:text-zinc-900 transition-colors"
        >
          {collapsed ? <CaretRight size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
          {Icon && <Icon size={14} className={accent || 'text-zinc-500'} />}
          <span>{title}</span>
          <span className="ml-auto text-[10px] uppercase tracking-wider text-zinc-400">
            {collapsed ? 'Klap uit' : 'Klap in'}
          </span>
        </button>
        {rightAdornment && <div className="flex items-center gap-1">{rightAdornment}</div>}
      </div>
      {!collapsed && <div className="mt-3" data-testid={`collapsible-body-${id}`}>{children}</div>}
    </div>
  );
}
